// PDF + Excel export helpers
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { money, formatDate, clienteName, proyectoName, proyectoTotal } from "@/lib/helpers";

const stamp = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
};

export const flattenFor = (col, state) => {
  const rows = state[col] || [];
  if (col === "proyectos")
    return rows.map((p) => ({
      Numero: p.numero || "",
      Nombre: p.nombre,
      Cliente: clienteName(state, p.clienteId),
      FechaInicio: p.fechaInicio,
      FechaFin: p.fechaFin,
      Estado: p.estado,
      Progreso: (p.progreso || 0) + "%",
      Articulos: (p.articulos || []).length,
      Servicios: (p.servicios || []).length,
      Personal: (p.personalIds || []).length,
      Total: proyectoTotal(p),
    }));
  if (col === "ventas")
    return rows.map((v) => ({
      Numero: v.numero,
      Fecha: v.fecha,
      Cliente: clienteName(state, v.clienteId),
      Proyecto: proyectoName(state, v.proyectoId),
      Lineas: (v.items || []).length + (v.serviciosItems || []).length,
      IVA: v.iva,
      Total: v.total,
      Estado: v.estado,
    }));
  return rows.map((r) => {
    const { id, ...rest } = r;
    return rest;
  });
};

export const exportExcel = (col, state) => {
  const rows = flattenFor(col, state);
  if (rows.length === 0) return false;
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, col);
  XLSX.writeFile(wb, `convitel-${col}-${stamp()}.xlsx`);
  return true;
};

export const exportPDF = (col, state) => {
  const rows = flattenFor(col, state);
  if (rows.length === 0) return false;
  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(14);
  doc.setTextColor(247, 148, 29);
  doc.text("CONVITEL · " + col.toUpperCase(), 14, 14);
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text("Generado: " + new Date().toLocaleString("es-ES"), 14, 20);
  const head = [Object.keys(rows[0])];
  const body = rows.map((r) => Object.values(r).map((v) => (v == null ? "" : String(v))));
  autoTable(doc, {
    head,
    body,
    startY: 26,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [247, 148, 29] },
  });
  doc.save(`convitel-${col}-${stamp()}.pdf`);
  return true;
};

export const ventaPDF = (v, state) => {
  const doc = new jsPDF();
  doc.setFontSize(18);
  doc.setTextColor(247, 148, 29);
  doc.text("CONVITEL", 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(60);
  doc.text("Comunicaciones", 14, 24);
  doc.setFontSize(14);
  doc.setTextColor(0);
  doc.text(`Venta ${v.numero}`, 140, 18);
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text(`Fecha: ${formatDate(v.fecha)}`, 140, 25);
  doc.text(`Cliente: ${clienteName(state, v.clienteId)}`, 140, 30);
  if (v.proyectoId) doc.text(`Proyecto: ${proyectoName(state, v.proyectoId)}`, 140, 35);
  const artRows = (v.items || []).map((l) => {
    const a = state.articulos.find((x) => x.id === l.articuloId) || {};
    return [
      a.codigo || l.codigo || "",
      a.nombre || l.nombre || "",
      l.cantidad,
      money(l.precio),
      money((+l.cantidad || 0) * (+l.precio || 0)),
    ];
  });
  const servRows = (v.serviciosItems || []).map((l) => {
    const s = state.servicios.find((x) => x.id === l.servicioId) || {};
    return [
      s.codigo || l.codigo || "",
      s.nombre || l.nombre || "",
      l.cantidad,
      money(l.precio),
      money((+l.cantidad || 0) * (+l.precio || 0)),
    ];
  });
  autoTable(doc, {
    head: [["Código", "Concepto", "Cant.", "Precio", "Subtotal"]],
    body: [...artRows, ...servRows],
    startY: 45,
    headStyles: { fillColor: [41, 171, 226] },
    styles: { fontSize: 9 },
  });
  const fy = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(10);
  const base = ((v.items || []).reduce((s, l) => s + (+l.cantidad || 0) * (+l.precio || 0), 0) +
    (v.serviciosItems || []).reduce((s, l) => s + (+l.cantidad || 0) * (+l.precio || 0), 0));
  doc.text(`Base: ${money(base)}`, 140, fy);
  doc.text(`IVA ${v.iva || 0}%: ${money((base * (v.iva || 0)) / 100)}`, 140, fy + 6);
  doc.setFontSize(13);
  doc.setTextColor(247, 148, 29);
  doc.text(`TOTAL: ${money(v.total || base * (1 + (v.iva || 0) / 100))}`, 140, fy + 14);
  doc.save(`convitel-venta-${v.numero}.pdf`);
};


// ----- Individual venta as Excel (multi-sheet) -----
export const ventaExcel = (v, state) => {
  const wb = XLSX.utils.book_new();

  const base = (v.items || []).reduce((s, l) => s + (+l.cantidad || 0) * (+l.precio || 0), 0)
             + (v.serviciosItems || []).reduce((s, l) => s + (+l.cantidad || 0) * (+l.precio || 0), 0);
  const iva = (base * (+v.iva || 0)) / 100;
  const total = v.total || base + iva;

  const info = [
    ["Número", v.numero || ""],
    ["Fecha", v.fecha || ""],
    ["Cliente", clienteName(state, v.clienteId)],
    ["Proyecto", v.proyectoId ? proyectoName(state, v.proyectoId) : ""],
    ["Estado", v.estado || ""],
    ["Base", base],
    ["IVA (%)", +v.iva || 0],
    ["IVA (€)", iva],
    ["TOTAL", total],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([["Campo", "Valor"], ...info]), "Venta");

  const articulos = (v.items || []).map((l) => {
    const a = state.articulos.find((x) => x.id === l.articuloId) || {};
    const cant = +l.cantidad || 0, pr = +l.precio || 0;
    return {
      Codigo: a.codigo || l.codigo || "",
      Articulo: a.nombre || l.nombre || "",
      Cantidad: cant,
      Precio: pr,
      Subtotal: cant * pr,
    };
  });
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(articulos.length ? articulos : [{ Codigo: "", Articulo: "(sin artículos)", Cantidad: 0, Precio: 0, Subtotal: 0 }]), "Artículos");

  const servicios = (v.serviciosItems || []).map((l) => {
    const s = state.servicios.find((x) => x.id === l.servicioId) || {};
    const cant = +l.cantidad || 0, pr = +l.precio || 0;
    return {
      Codigo: s.codigo || l.codigo || "",
      Servicio: s.nombre || l.nombre || "",
      Cantidad: cant,
      Precio: pr,
      Subtotal: cant * pr,
    };
  });
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(servicios.length ? servicios : [{ Codigo: "", Servicio: "(sin servicios)", Cantidad: 0, Precio: 0, Subtotal: 0 }]), "Servicios");

  XLSX.writeFile(wb, `convitel-venta-${v.numero}.xlsx`);
};


// ----- Individual proyecto as PDF -----
export const proyectoPDF = (p, state) => {
  const doc = new jsPDF();
  doc.setFontSize(18);
  doc.setTextColor(247, 148, 29);
  doc.text("CONVITEL", 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(60);
  doc.text("Comunicaciones", 14, 24);

  doc.setFontSize(14);
  doc.setTextColor(0);
  doc.text(`Proyecto ${p.numero || ""}`, 140, 18);
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text(`Cliente: ${clienteName(state, p.clienteId)}`, 140, 25);
  doc.text(`Estado: ${p.estado || "-"}`, 140, 30);
  doc.text(`Progreso: ${Math.max(0, Math.min(100, +p.progreso || 0))}%`, 140, 35);

  // Project header info
  doc.setFontSize(11);
  doc.setTextColor(0);
  doc.text(p.nombre || "(Sin nombre)", 14, 40);
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text(`Inicio: ${formatDate(p.fechaInicio)}   Fin: ${formatDate(p.fechaFin)}   Creación: ${formatDate(p.fecha)}`, 14, 46);
  if (p.descripcion) {
    doc.text(`Descripción: ${p.descripcion}`, 14, 52, { maxWidth: 180 });
  }

  let y = p.descripcion ? 58 : 52;

  // Articles
  const artRows = (p.articulos || []).map((l) => {
    const a = state.articulos.find((x) => x.id === l.articuloId) || {};
    const cant = +l.cantidad || 0, pr = +l.precio || 0;
    return [
      a.codigo || l.codigo || "",
      a.nombre || l.nombre || "",
      cant,
      money(pr),
      money(cant * pr),
    ];
  });
  if (artRows.length) {
    autoTable(doc, {
      head: [["Código", "Artículo", "Cant.", "Precio", "Subtotal"]],
      body: artRows,
      startY: y,
      headStyles: { fillColor: [247, 148, 29] },
      styles: { fontSize: 9 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // Services
  const servRows = (p.servicios || []).map((l) => {
    const s = state.servicios.find((x) => x.id === l.servicioId) || {};
    const cant = +l.cantidad || 0, pr = +l.precio || 0;
    return [
      s.codigo || l.codigo || "",
      s.nombre || l.nombre || "",
      cant,
      money(pr),
      money(cant * pr),
    ];
  });
  if (servRows.length) {
    autoTable(doc, {
      head: [["Código", "Servicio", "Cant.", "Precio", "Subtotal"]],
      body: servRows,
      startY: y,
      headStyles: { fillColor: [41, 171, 226] },
      styles: { fontSize: 9 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // Personnel
  const persRows = (p.personalIds || []).map((pid) => {
    const pers = state.personal.find((x) => x.id === pid) || {};
    return [
      pers.nombre || "(empleado eliminado)",
      pers.dni || "",
      pers.telefono || "",
      pers.email || "",
    ];
  });
  if (persRows.length) {
    autoTable(doc, {
      head: [["Nombre", "DNI", "Teléfono", "Email"]],
      body: persRows,
      startY: y,
      headStyles: { fillColor: [16, 185, 129] },
      styles: { fontSize: 9 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // Totals
  const subA = (p.articulos || []).reduce((s, l) => s + (+l.cantidad || 0) * (+l.precio || 0), 0);
  const subS = (p.servicios || []).reduce((s, l) => s + (+l.cantidad || 0) * (+l.precio || 0), 0);
  const tot = subA + subS;
  doc.setFontSize(10);
  doc.setTextColor(60);
  doc.text(`Subtotal artículos: ${money(subA)}`, 130, y + 4);
  doc.text(`Subtotal servicios: ${money(subS)}`, 130, y + 10);
  doc.setFontSize(13);
  doc.setTextColor(247, 148, 29);
  doc.text(`TOTAL: ${money(tot)}`, 130, y + 18);

  doc.save(`convitel-proyecto-${(p.numero || p.id || "sin-num").replace(/[^a-zA-Z0-9-_]/g, "_")}.pdf`);
};


// ----- Individual proyecto as Excel (multi-sheet) -----
export const proyectoExcel = (p, state) => {
  const wb = XLSX.utils.book_new();

  const subA = (p.articulos || []).reduce((s, l) => s + (+l.cantidad || 0) * (+l.precio || 0), 0);
  const subS = (p.servicios || []).reduce((s, l) => s + (+l.cantidad || 0) * (+l.precio || 0), 0);

  const info = [
    ["Número", p.numero || ""],
    ["Nombre", p.nombre || ""],
    ["Cliente", clienteName(state, p.clienteId)],
    ["Estado", p.estado || ""],
    ["Progreso (%)", Math.max(0, Math.min(100, +p.progreso || 0))],
    ["Fecha creación", p.fecha || ""],
    ["Fecha inicio", p.fechaInicio || ""],
    ["Fecha fin", p.fechaFin || ""],
    ["Descripción", p.descripcion || ""],
    ["Subtotal artículos", subA],
    ["Subtotal servicios", subS],
    ["TOTAL", subA + subS],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([["Campo", "Valor"], ...info]), "Proyecto");

  const articulos = (p.articulos || []).map((l) => {
    const a = state.articulos.find((x) => x.id === l.articuloId) || {};
    const cant = +l.cantidad || 0, pr = +l.precio || 0;
    return {
      Codigo: a.codigo || l.codigo || "",
      Articulo: a.nombre || l.nombre || "",
      Cantidad: cant,
      Precio: pr,
      Subtotal: cant * pr,
    };
  });
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(articulos.length ? articulos : [{ Codigo: "", Articulo: "(sin artículos)", Cantidad: 0, Precio: 0, Subtotal: 0 }]), "Artículos");

  const servicios = (p.servicios || []).map((l) => {
    const s = state.servicios.find((x) => x.id === l.servicioId) || {};
    const cant = +l.cantidad || 0, pr = +l.precio || 0;
    return {
      Codigo: s.codigo || l.codigo || "",
      Servicio: s.nombre || l.nombre || "",
      Cantidad: cant,
      Precio: pr,
      Subtotal: cant * pr,
    };
  });
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(servicios.length ? servicios : [{ Codigo: "", Servicio: "(sin servicios)", Cantidad: 0, Precio: 0, Subtotal: 0 }]), "Servicios");

  const personal = (p.personalIds || []).map((pid) => {
    const pers = state.personal.find((x) => x.id === pid) || {};
    return {
      Nombre: pers.nombre || "(empleado eliminado)",
      DNI: pers.dni || "",
      Telefono: pers.telefono || "",
      Email: pers.email || "",
      Vehiculo: pers.vehiculo || "",
      Matricula: pers.matricula || "",
    };
  });
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(personal.length ? personal : [{ Nombre: "(sin personal)", DNI: "", Telefono: "", Email: "", Vehiculo: "", Matricula: "" }]), "Personal");

  XLSX.writeFile(wb, `convitel-proyecto-${(p.numero || p.id || "sin-num").replace(/[^a-zA-Z0-9-_]/g, "_")}.xlsx`);
};
